'use client'

import { useAuthorization } from '@entities/authentication'
import type { KrPaymentOrder } from '@entities/kr-payment/model'
import { generateMerchantUid, usePaymentErrorStaticModel } from '@entities/payment'
import { PaymentMethodType } from '@entities/payment-methods'
import { userEntityQueries } from '@entities/users'
import {
  useCheckPaymentStatusPolling,
  usePaymentByCreditCard,
  usePaymentByKakaoPay,
  usePaymentQueue,
  usePaymentValidation,
} from '@features/payment'
import { useRegisterCardFormState } from '@features/payment-methods'
import { PaymentType, SubscribeTicket, TrialLevel } from '@server/schemas'
import { authenticatedFetch } from '@shared/apis/authenticated-fetch'
import { env } from '@shared/config/env'
import { success, withAsyncPipeline, withSuccessCallback } from '@shared/libs'
import { sendGA4PurchaseEvent } from '@shared/libs/gtm'
import { getMetaContextHeaders } from '@shared/marketing/meta-context'
import { useNativeBridgeStore } from '@shared/models'
import { buildBookingUrl, PAID_TRIAL_BOOKING_REFERRER } from '@shared/routes'
import { openAlertDialog } from '@shared/ui'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useMemo, useRef } from 'react'

import { useKrPreparedPayment } from './use-kr-prepared-payment'

type PaymentInfo = {
  isTermsAgreed: boolean
  paymentType: PaymentType
  paymentMethodType: Nullable<PaymentMethodType>
  customerUid: Nullable<string>
  amount: number
  trialLevel: TrialLevel
  subscribeTicket: SubscribeTicket
  merchantName: string
  isFree?: boolean
  productId?: string
  krPreparedCheckout?: boolean
  preparedOrderStatus?: KrPaymentOrder['status']
}

export const useTrialPayment = ({
  isTermsAgreed,
  paymentType,
  paymentMethodType,
  customerUid,
  trialLevel,
  subscribeTicket,
  merchantName,
  isFree = false,
  amount,
  productId,
  krPreparedCheckout = false,
  preparedOrderStatus,
}: PaymentInfo) => {
  const authorization = useAuthorization()
  const nativeStore = useNativeBridgeStore()
  const { ERROR_MESSAGES } = usePaymentErrorStaticModel()

  const { data: userInfo } = useSuspenseQuery({
    ...userEntityQueries.getCurrentUser({ bearerToken: authorization.bearerToken }),
  })

  const sendPurchaseEvent = (): 'success' | 'error' => {
    const paymentAmount = isFree ? 0 : amount

    // GTM 커스텀 purchase 이벤트 전송
    sendGA4PurchaseEvent({
      transactionId: `T_${Date.now()}${Math.random()}`,
      value: paymentAmount,
      currency: 'KRW',
      items: [
        {
          itemId: `podo_trial_${subscribeTicket.langType}`,
          itemName: `podo_trial_${subscribeTicket.langType}`,
          itemCategory: 'Trial',
          price: paymentAmount,
          quantity: 1,
        },
      ],
      userId: userInfo?.id,
      userName: userInfo?.name,
      userPhone: userInfo?.phone,
      userEmail: userInfo?.email,
    })

    return success()
  }

  const hasSentPurchaseEventRef = useRef(false)
  const sendPurchaseEventOnce = (): 'success' | 'error' => {
    if (hasSentPurchaseEventRef.current) {
      return success()
    }
    hasSentPurchaseEventRef.current = true
    return sendPurchaseEvent()
  }

  const { pollingState, setPollingState } = useCheckPaymentStatusPolling({
    onSuccess: async (extraData) => {
      if (krPreparedCheckout) {
        sessionStorage.removeItem(`podo:kr-purchase:${authorization.uid}:${subscribeTicket.id}`)
      }
      // 리다이렉트 전에 구매 이벤트 전송
      sendPurchaseEventOnce()

      if (!extraData?.classId || !authorization.token) {
        throw new Error('classId not found')
      }

      await new Promise((resolve) => setTimeout(resolve, 300))

      const baseUrl = buildBookingUrl({
        type: 'new',
        classId: extraData.classId,
        classType: 'regular',
        referrer: PAID_TRIAL_BOOKING_REFERRER,
      })
      const appended = new URLSearchParams({
        langType: String(subscribeTicket.langType),
        random: String(authorization.token + Math.floor(Math.random() * 1000000)),
      }).toString()

      window.location.href = `${baseUrl}&${appended}`
    },
    isFree,
  })

  const createNotificationWebhookUrl = (): URL => {
    const webhookUrl = new URL(`${env.NEXT_PUBLIC_PODO_API_URL}/api/v1/payment/podo/webhook`)

    webhookUrl.searchParams.set('user_id', authorization.uid.toString())
    webhookUrl.searchParams.set('subscribe_id', subscribeTicket.id)
    webhookUrl.searchParams.set('trial_level', trialLevel)
    if (productId) {
      webhookUrl.searchParams.set('product_id', productId)
    }
    webhookUrl.searchParams.set('payment_type', 'TRIAL')

    return webhookUrl
  }

  const billingFailSlackMessage = (errorCode?: string, errorMessage?: string) => {
    return `[Trial lesson ticket payment API failed] User: ${userInfo.name}(${userInfo.id}) / Amount: ₩${subscribeTicket.subPrice.toLocaleString()}
[${errorCode}] ${errorMessage}

\`System info\`
  - \`OS\` : ${nativeStore.os}
  - \`UserAgent\` : ${nativeStore.userAgent}
  - \`RuntimeVersion\` : ${nativeStore.runtimeVersion}
  - \`from\` : ${nativeStore.from}`
  }
  const handlePaymentQueue = usePaymentQueue({
    handleEnabledPolling: (merchantUid) =>
      setPollingState({
        status: 'in-progress',
        data: { merchantUid, ...(krPreparedCheckout ? { purchaseOrderId: merchantUid } : {}) },
      }),
  })

  const { formState: registerCardFormState } = useRegisterCardFormState()

  const { validateClientSideRegisterCard, validateServerSideRegisterCard, validatePayment } = usePaymentValidation({
    isTermsAgreed,
    paymentMethodType,
    registerCardFormState,
    paymentType,
  })

  const preparePayment = useKrPreparedPayment({
    enabled: krPreparedCheckout && !isFree,
    subscribeId: subscribeTicket.id,
    productId,
    amount,
    couponId: null,
    paymentMethod: paymentMethodType,
    isLumpSum: false,
    trialLevel,
    captureTrialContext: true,
    onPending: (merchantUid) =>
      setPollingState({ status: 'in-progress', data: { merchantUid, purchaseOrderId: merchantUid } }),
  })

  const customData = useMemo(
    () => ({
      user_id: authorization.uid.toString(),
      subscribe_id: subscribeTicket.id,
      product_id: productId,
      coupon_id: null,
      is_v2_validated: false,
    }),
    [authorization.uid, subscribeTicket.id, productId],
  )

  const { handlePaymentForNewCreditCard, handlePaymentForRegisteredCreditCard } = usePaymentByCreditCard({
    captureTrialContext: true,
    preparePayment,
    handlePaymentQueue,
    validatePayment,
    validateClientSideRegisterCard,
    validateServerSideRegisterCard,
    customerUid,
    merchantName: merchantName,
    amount,
    customData,
    paymentType: 'TRIAL',
    billingFailSlackMessage,
    paymentWebhookUrl: createNotificationWebhookUrl(),
  })

  const { handlePaymentForNewKakaoPay, handlePaymentForRegisteredKakaoPay } = usePaymentByKakaoPay({
    captureTrialContext: true,
    preparePayment,
    validatePayment,
    handlePaymentQueue,
    customerUid,
    merchantName: merchantName,
    amount,
    customData,
    paymentType: 'TRIAL',
    billingFailSlackMessage,
    couponId: null,
    paymentWebhookUrl: createNotificationWebhookUrl(),
    mobileRedirectSearchParams: {
      __brand: 'TRIAL',
      trial_level: trialLevel,
      subscribe_id: subscribeTicket.id,
    },
  })

  const handleTrialFreePayment = async (): Promise<'success' | 'error'> => {
    if (!userInfo?.id) {
      throw new Error('User info not found')
    }

    const merchantUid = generateMerchantUid(userInfo.id.toString())

    try {
      // BFF API 호출 - 백엔드 웹훅을 직접 호출
      const response = await authenticatedFetch(`${env.NEXT_PUBLIC_API_URL}/api/v1/payment/trial-free`, {
        method: 'POST',
        headers: {
          ...getMetaContextHeaders(userInfo.countryCode),
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authorization.bearerToken}`,
        },
        body: JSON.stringify({
          subscribeId: subscribeTicket.id,
          trialLevel: trialLevel,
          merchantUid: merchantUid,
          productId,
        }),
      })

      if (!response.ok) {
        throw new Error('Trial free payment API failed')
      }

      const result = (await response.json()) as { status: string; error?: string }

      if (result.status !== 'success') {
        throw new Error(result.error || 'Trial free payment failed')
      }

      // 폴링 시작
      setPollingState({ status: 'in-progress', data: { merchantUid } })

      return success()
    } catch (error) {
      console.error('Trial free payment failed:', error)
      // 발급 실패를 사용자에게 즉시 알린다 — 조용히 실패하면 아무 반응 없는 화면으로 오인된다.
      openAlertDialog({ title: ERROR_MESSAGES.FREE_TRIAL_PAYMENT_FAILED })
      return 'error'
    }
  }

  return {
    resumePreparedPayment:
      krPreparedCheckout &&
      preparedOrderStatus &&
      ['PROCESSING', 'FULFILLMENT_PENDING', 'PAID'].includes(preparedOrderStatus)
        ? async () => {
            await preparePayment?.()
          }
        : undefined,
    handlePaymentForNewCreditCard: withAsyncPipeline(
      handlePaymentForNewCreditCard,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    handlePaymentForRegisteredCreditCard: withAsyncPipeline(
      handlePaymentForRegisteredCreditCard,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    handlePaymentForNewKakaoPay: withAsyncPipeline(
      handlePaymentForNewKakaoPay,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    handlePaymentForRegisteredKakaoPay: withAsyncPipeline(
      handlePaymentForRegisteredKakaoPay,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    handleTrialFreePayment: withAsyncPipeline(
      handleTrialFreePayment,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    validatePayment,
    pollingState,
  }
}
